import React, { useState, useEffect, useCallback } from 'react';
import Pagination from '@mui/material/Pagination';
import { Link } from 'react-router-dom';
import { API_URLS } from '../../../apiUrl';
import axiosInstance from '../../../components/axiosInstance';

interface StreetWiseHoldingNoProps {
  municipalityId: string;
  streetViewId: boolean; // Change to boolean
  breadCrumbHolding: (value: boolean) => void;
  breadCrumbWard: (value: boolean) => void;
  breadCrumbBorrow: (value: boolean) => void;
  municipalityName: string;
}

interface HoldingData {
  holding_no: string;
  holding_type: string;
  name_of_complex_apartment: string;
  location_name: string;
  property_khatiyan_no: string;
  total_cover_area: string;
  ownership: string;
}

const ITEMS_PER_PAGE = 50;

const StreetWiseHoldingNo: React.FC<StreetWiseHoldingNoProps> = ({
  municipalityId,
  streetViewId,
  breadCrumbHolding,
  breadCrumbWard,
  breadCrumbBorrow,
  municipalityName,
}) => {
  const [tableData, setTableData] = useState<HoldingData[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  const isRole = localStorage.getItem('role_type');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post(API_URLS.GetStreetWiseHoldingNo, {
        municipalityTbl_id: municipalityId,
        street_tbl_id: streetViewId,
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      });

      if (response.data.data) {
        setTableData(response.data.data.data || []);
        setTotalItems(response.data.data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setTableData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [municipalityId, streetViewId, currentPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredData = tableData.filter((item) =>
    Object.values(item).some((value) =>
      value?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  return (
    <>
      <nav className="bg-gradient-to-r from-gray-200 to-gray-100 py-3 px-4 mb-3 rounded-lg">
        <ul className="flex items-center space-x-4 text-sm text-gray-700">
          {isRole !== 'Municipality Admin' && (
            <>
              <li>
                <Link
                  to="/dashboard/overview"
                  className="hover:text-blue-600 hover:underline transition duration-300 ease-in-out"
                >
                  Home
                </Link>
              </li>
              <li>
                <span className="text-gray-400">/</span>
                <Link
                  to="/dashboard/view-municipality"
                  className="hover:text-blue-600 hover:underline transition duration-300 ease-in-out ml-2"
                >
                  Municipalities
                </Link>
              </li>
            </>
          )}
          <li>
            {isRole !== 'Municipality Admin' && <span className="text-gray-400">/</span>}
            <button
              onClick={() => breadCrumbBorrow(false)}
              className="hover:text-blue-600 hover:underline transition duration-300 ease-in-out ml-2"
            >
              Borough
            </button>
          </li>
          <li>
            <span className="text-gray-400">/</span>
            <button
              onClick={() => breadCrumbWard(false)}
              className="hover:text-blue-600 hover:underline transition duration-300 ease-in-out ml-2"
            >
              Ward
            </button>
          </li>
          <li>
            <span className="text-gray-400">/</span>
            <button
              onClick={() => breadCrumbHolding(false)}
              className="hover:text-blue-600 hover:underline transition duration-300 ease-in-out ml-2"
            >
              Street
            </button>
          </li>
          <li>
            <span className="text-gray-400">/</span>
            <span className="font-semibold text-gray-800 ml-2">Holding No. List</span>
          </li>
        </ul>
      </nav>

      <div className="container mx-auto">
        <div className="flex gap-5 mb-4">
          <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
            Holding No. List ({municipalityName})
          </h1>

          <div className="flex justify-end md:w-4/12 w-full ml-auto">
            <input
              type="text"
              placeholder="Search..."
              className="border px-3 py-2 rounded-lg w-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-900 dark:bg-gray-800"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto bg-white rounded-lg shadow-lg border border-gray-200 dark:border-gray-800">
          <table className="min-w-full table-auto">
            <thead className="bg-gray-100 border-b dark:border-gray-600 dark:bg-gray-700">
              <tr>
                <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">
                  Holding No.
                </th>
                <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">
                  Holding Type
                </th>
                <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">
                  Apartment/Complex Name
                </th>
                <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">
                  Property Location
                </th>
                <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">
                  Property Khatiyan No.
                </th>
                <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">
                  Total Cover Area
                </th>
                <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">
                  Owner Name
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-3 px-6 text-center">
                    Loading...
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-3 px-6 text-center dark:bg-gray-800">
                    No results found
                  </td>
                </tr>
              ) : (
                filteredData.map((item, index) => (
                  <tr
                    key={index}
                    className="border-b dark:border-gray-700 hover:bg-gray-50"
                  >
                    <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">
                      {item.holding_no || 'N/A'}
                    </td>
                    <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">
                      {item.holding_type || 'N/A'}
                    </td>
                    <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">
                      {item.name_of_complex_apartment || 'N/A'}
                    </td>
                    <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">
                      {item.location_name || 'N/A'}
                    </td>
                    <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">
                      {item.property_khatiyan_no || 'N/A'}
                    </td>
                    <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">
                      {item.total_cover_area || 'N/A'}
                    </td>
                    <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">
                      {item.ownership || 'N/A'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filteredData.length > 0 && (
          <div className="flex justify-center mt-4">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
            />
          </div>
        )}
      </div>
    </>
  );
};

export default StreetWiseHoldingNo;